// src/pages/CheckInOutPage.js
import React, { useState, useEffect, useRef, useMemo } from "react";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import {
  Button,
  Space,
  Typography,
  Table,
  Input,
  Form,
  DatePicker,
  Modal,
  Alert,
  Descriptions,
} from "antd";
import {
} from "react-icons/fa";
import { DetailList, PageHeader, RowActions, SectionCard, StatusTag, tablePagination, useFlatPage } from "../components/ui";
import { useReactToPrint } from "react-to-print";
import InvoiceModal from "../components/InvoiceModal";
import moment from "moment";
import "./CheckInOutPage.css";
import "./CheckPages.css";
import { IconCheck, IconInfo, IconRefresh, IconSearch } from "../components/ui/icons";
import notify from "../utils/notify";

const { Text, Title } = Typography;

const num = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};

const str = (v) => (v == null ? "" : String(v));

const pick = (...vals) => {
  for (const v of vals) {
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return undefined;
};

const CheckInOutPage = () => {
  useFlatPage();
  const { authData } = useAuth();
  const hotelId = authData?.hotelId;

  const [form] = Form.useForm();
  const invoiceRef = useRef(null);

  const [hotelDetails, setHotelDetails] = useState({
    hotelName: "",
    address: "",
    phoneNumber: "",
  });

  const [guestDetails, setGuestDetails] = useState({
    name: "",
    firstName: "",
    lastName: "",
    notes: "",
    roomNo: "",
    bookingNo: "",
    bookingId: 0,
    email: "",
    mobile: "",
    address: "",
    timeFormat: "24HRS",
    pendingAmount: 0,
    paymentAmount: 0,
    basePrice: 0,
    vatPercentage: 0,
    additionalLevies: 0,
    daysBooked: 1,
    totalAmount: 0,
    transactionType: "",
    currency: "BWP",
  });

  const [bookings, setBookings] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusMessage, setStatusMessage] = useState({ type: "", content: "" });

  const [loading, setLoading] = useState({
    search: false,
    checkins: false,
    checkIn: false,
    checkOut: false,
  });

  const [modalVisible, setModalVisible] = useState({
    checkIn: false,
    checkOut: false,
    invoice: false,
  });

  const [dates, setDates] = useState({
    checkIn: null,
    checkOut: null,
  });

  const showApiError = (error, fallback = "Request failed. Please try again.") => {
    // AntD form validation errors
    if (error?.errorFields?.length) {
      const first = error.errorFields[0];
      notify.error({
        message: "Validation Error",
        description: first?.errors?.[0] || "Please check the form fields.",
      });
      return;
    }

    console.error("API Error:", error);

    const apiErrors = error?.response?.data?.errors;
    const apiMessage =
      error?.response?.data?.message ||
      (Array.isArray(apiErrors) ? apiErrors.join(", ") : null) ||
      error?.message ||
      fallback;

    notify.error({
      message: "Error",
      description: apiMessage,
    });
  };

  // Fetch hotel details
  useEffect(() => {
    const fetchHotelDetails = async () => {
      if (!hotelId) return;
      try {
        const response = await axiosInstance.get(`/Hotel/${hotelId}`);
        setHotelDetails({
          hotelName: response.data?.hotelName || "Hotel Name",
          address: response.data?.address || "Hotel Address",
          phoneNumber: response.data?.contactNumber || "+000 000 0000",
        });
      } catch (error) {
        console.error("Error fetching hotel details:", error);
      }
    };
    fetchHotelDetails();
  }, [hotelId]);

  // Fetch current check-ins
  const fetchCheckins = async () => {
    if (!hotelId) {
      notify.error({
        message: "Error",
        description: "Hotel ID is missing. Please log in again.",
      });
      return;
    }

    setLoading((prev) => ({ ...prev, checkins: true }));
    try {
      const response = await axiosInstance.get(`/hotels/${hotelId}/Status/checkedin`);
      setCheckins(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      showApiError(error, "Failed to fetch check-ins.");
    } finally {
      setLoading((prev) => ({ ...prev, checkins: false }));
    }
  };

  useEffect(() => {
    fetchCheckins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId]);

  // Search guest handler
  const handleSearchGuest = async () => {
    const trimmed = searchTerm.trim();
    if (!trimmed) {
      notify.warning({ message: "Warning", description: "Please enter a valid ID Number." });
      return;
    }
    if (!hotelId) {
      notify.error({ message: "Error", description: "Hotel ID is missing. Please log in again." });
      return;
    }

    setLoading((prev) => ({ ...prev, search: true }));
    setStatusMessage({ type: "", content: "" });

    try {
      const [guestResponse, bookingsResponse] = await Promise.all([
        axiosInstance.get("/Guests/SearchByIdNumberAndHotel", {
          params: { idNumber: trimmed, hotelId },
        }),
        axiosInstance.get("/Bookings/SearchBookingsByGuest", {
          params: {
            idNumber: trimmed,
            hotelId,
            includeStatuses: [1], // Confirmed=1 (adjust to your enum)
          },
        }),
      ]);

      const guest = guestResponse.data;
      const rawBookings = Array.isArray(bookingsResponse.data)
        ? bookingsResponse.data
        : bookingsResponse.data
          ? [bookingsResponse.data]
          : [];

      if (!guest) {
        notify.warning({ message: "Not Found", description: "Guest not found." });
        return;
      }

      if (!rawBookings.length) {
        notify.warning({ message: "No Bookings", description: "No bookings found for the guest." });
        return;
      }

      const firstBooking = rawBookings[0];

      // STRONGER fallback mapping for BookingId/BookingNo/RoomId
      const bookingId = num(
        pick(
          firstBooking.bookingId,
          firstBooking.BookingId,
          firstBooking.bookingID,
          firstBooking.BookingID,
          firstBooking.id,
          firstBooking.Id
        ),
        0
      );

      const bookingNo = str(
        pick(firstBooking.bookingNo, firstBooking.BookingNo, firstBooking.bookingNumber, firstBooking.BookingNumber)
      );

      const roomId = num(pick(firstBooking.roomId, firstBooking.RoomId, firstBooking.roomID, firstBooking.RoomID), 0);

      // Try to get room number from booking first, else fetch room
      let roomNumber = str(pick(firstBooking.roomNumber, firstBooking.RoomNumber));
      if (!roomNumber && roomId > 0) {
        const roomRes = await axiosInstance.get(`/Rooms/${roomId}`);
        roomNumber = str(pick(roomRes.data?.roomNumber, roomRes.data?.RoomNumber, "Unknown"));
      }

      const totalAmount = num(pick(firstBooking.totalAmount, firstBooking.TotalAmount), 0);
      const paidAmount = num(pick(firstBooking.paidAmount, firstBooking.PaidAmount), 0);
      const pendingAmount = Math.max(totalAmount - paidAmount, 0);

      const currency = str(pick(firstBooking.currency, firstBooking.Currency, "BWP"));

      setGuestDetails({
        name: `${str(guest.firstName)} ${str(guest.lastName)}`.trim(),
        firstName: str(guest.firstName),
        lastName: str(guest.lastName),
        notes: "",
        roomNo: roomNumber || "Unknown",
        bookingNo,
        bookingId,
        email: str(guest.email),
        mobile: guest.phoneNumber?.trim() ? str(guest.phoneNumber) : "70000000",
        address: str(guest.address),
        pendingAmount,
        timeFormat: "24HRS",
        paymentAmount: 0,
        basePrice: num(pick(firstBooking.basePrice, firstBooking.BasePrice), 0),
        vatPercentage: num(pick(firstBooking.vatPercentage, firstBooking.VatPercentage), 0),
        additionalLevies: num(pick(firstBooking.additionalLevies, firstBooking.AdditionalLevies), 0),
        daysBooked: num(pick(firstBooking.daysBooked, firstBooking.DaysBooked), 1),
        totalAmount,
        transactionType: str(pick(firstBooking.transactionType, firstBooking.TransactionType)),
        currency,
      });

      setBookings(rawBookings);

      setDates({
        checkIn: firstBooking.checkInDate ? moment(firstBooking.checkInDate) : null,
        checkOut: firstBooking.checkOutDate ? moment(firstBooking.checkOutDate) : null,
      });

      // Make sure modal form values are reset (initialValues won't update on its own)
      form.setFieldsValue({ notes: "", paymentAmount: 0 });

      notify.success({ message: "Success", description: "Guest and booking details fetched successfully." });

      if (bookingId <= 0) {
        notify.warning({
          message: "Missing BookingId",
          description:
            "Your bookings endpoint did not return BookingId. Check-in will fail until BookingId is included or backend validation is relaxed.",
        });
      }
    } catch (error) {
      showApiError(error, "Error fetching details. Please try again.");
    } finally {
      setLoading((prev) => ({ ...prev, search: false }));
    }
  };

  const openCheckInModal = () => {
    form.setFieldsValue({
      notes: guestDetails.notes || "",
      paymentAmount: guestDetails.paymentAmount || 0,
    });
    setModalVisible((prev) => ({ ...prev, checkIn: true }));
  };

  // ✅ FIXED: send full DTO required by backend validation
  const handleCheckIn = async () => {
    setLoading((prev) => ({ ...prev, checkIn: true }));
    try {
      const values = await form.validateFields();

      const bookingId = num(guestDetails.bookingId, 0);
      const bookingNo = str(guestDetails.bookingNo).trim();

      if (!bookingNo) {
        notify.error({
          message: "Missing Booking Number",
          description: "BookingNo is empty. Please search the guest again.",
        });
        return;
      }

      if (bookingId <= 0) {
        notify.error({
          message: "Invalid Booking ID",
          description:
            "BookingId must be greater than zero. Your bookings search API must return BookingId (or relax backend validation).",
        });
        return;
      }

      const pending = num(guestDetails.pendingAmount, 0);
      const payment = num(values.paymentAmount, 0);

      if (pending > 0 && payment < pending) {
        notify.error({
          message: "Payment Required",
          description: `Full payment is required to check-in. Pending: ${guestDetails.currency}${pending.toFixed(2)}`,
        });
        return;
      }

      // Keep local state in sync
      setGuestDetails((prev) => ({
        ...prev,
        paymentAmount: payment,
        notes: values.notes || "",
      }));

      // IMPORTANT: match your backend CheckInRequest validations
      const payload = {
        // These were required by your validation error list
        Name: guestDetails.name,
        FirstName: guestDetails.firstName,
        LastName: guestDetails.lastName,
        Email: guestDetails.email,
        Mobile: guestDetails.mobile,
        RoomNo: guestDetails.roomNo,
        Address: guestDetails.address,
        TimeFormat: guestDetails.timeFormat,

        // These are critical for server logic
        BookingId: bookingId,
        BookingNo: bookingNo,
        PaymentAmount: payment,
        Notes: values.notes || "",

        // Optional dates (include if your DTO has them)
        CheckInDate: dates.checkIn ? dates.checkIn.toISOString() : null,
        CheckOutDate: dates.checkOut ? dates.checkOut.toISOString() : null,

        // Your server code uses ExpectedCheckOutTime for extension logic
        ExpectedCheckOutTime: dates.checkOut ? dates.checkOut.toISOString() : null,
      };

      const res = await axiosInstance.post("/CheckIn", payload);

      notify.success({
        message: "Success",
        description: res?.data?.message || "Guest successfully checked in.",
      });

      setModalVisible((prev) => ({ ...prev, checkIn: false }));

      await fetchCheckins();
      await handleSearchGuest();
    } catch (error) {
      showApiError(error, "Check-in failed. Please try again.");
    } finally {
      setLoading((prev) => ({ ...prev, checkIn: false }));
    }
  };

  const handleViewInvoice = async (bookingId) => {
    try {
      const response = await axiosInstance.get(`/CheckIn/${bookingId}/invoice`);
      const invoiceData = response.data;

      setGuestDetails((prev) => ({
        ...prev,
        ...invoiceData,
        checkInDate: invoiceData.checkInDate,
        checkOutDate: invoiceData.checkOutDate,
      }));

      setModalVisible((prev) => ({ ...prev, invoice: true }));
    } catch (error) {
      showApiError(error, "Failed to fetch invoice data.");
    }
  };

  const handlePrint = useReactToPrint({
    content: () => invoiceRef.current,
    documentTitle: `Invoice_${guestDetails.bookingNo}`,
    onAfterPrint: () => notify.success({ message: "Invoice printed successfully" }),
  });

  const guestColumns = useMemo(
    () => [
      {
        title: "Booking ID",
        dataIndex: "bookingId",
        key: "bookingId",
        render: (id, record) => id || record?.BookingId || record?.bookingID || "N/A",
      },
      {
        title: "Room Number",
        dataIndex: "roomNumber",
        key: "roomNumber",
        render: (text, record) => text || record?.RoomNumber || "Unknown",
      },
      {
        title: "Status",
        key: "status",
        render: (_, record) => (
          <StatusTag status={record.status || record.Status}>{record.status || record.Status || "Unknown"}</StatusTag>
        ),
      },
    ],
    []
  );

  const checkinColumns = useMemo(
    () => [
      {
        title: "Guest",
        key: "name",
        render: (_, record) => (
          <div className="hms-cell-stack">
            <span className="hms-cell-main">
              {`${record.firstName || ""} ${record.lastName || ""}`.trim() || "—"}
            </span>
            <span className="hms-cell-sub">Booking #{record.bookingId || "—"}</span>
          </div>
        ),
      },
      {
        title: "Room",
        dataIndex: "roomNumber",
        key: "roomNumber",
        render: (text) => text || "—",
      },
      {
        title: "Check-in",
        key: "checkInDate",
        render: (_, record) => (record.checkInDate ? moment(record.checkInDate).format("DD MMM YYYY, HH:mm") : "N/A"),
      },
      {
        title: "Paid amount",
        key: "paidAmount",
        align: "right",
        render: (_, record) =>
          `${guestDetails.currency || "BWP"} ${num(record.paidAmount, 0).toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`,
      },
      {
        title: "",
        key: "actions",
        align: "right",
        render: (_, record) => (
          <RowActions
            actions={[
              {
                key: "invoice",
                label: "Invoice",
                icon: "document",
                showLabel: true,
                onClick: () => handleViewInvoice(record.bookingId),
              },
            ]}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleViewInvoice only calls the API and stable setters
    [guestDetails.currency]
  );

  return (
    <div className="hms-page checkin-page ci-page">
      <PageHeader
        actions={
          <>
            <Button
              icon={<IconInfo />}
              onClick={() =>
                notify.info({
                  message: "Info",
                  description: "Search guests by ID to manage check-ins and check-outs.",
                })
              }
            >
              Guide
            </Button>
            <Button icon={<IconRefresh />} onClick={fetchCheckins} loading={loading.checkins}>
              Refresh
            </Button>
          </>
        }
      />

      {statusMessage?.content && (
        <Alert type={statusMessage.type || "info"} message={statusMessage.content} showIcon />
      )}

      <SectionCard title="Find a guest">
        <Space.Compact className="ci-search">
          <Input
            placeholder="Enter guest ID number"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            maxLength={10}
            allowClear
            onPressEnter={handleSearchGuest}
          />
          <Button type="primary" icon={<IconSearch />} onClick={handleSearchGuest} loading={loading.search}>
            Search
          </Button>
        </Space.Compact>
      </SectionCard>

      {guestDetails.name && (
        <SectionCard
          title="Guest"
          extra={
            <Button type="primary" icon={<IconCheck />} onClick={openCheckInModal}>
              Check in
            </Button>
          }
        >
          <Descriptions column={2} size="small">
            <Descriptions.Item label="Name">{guestDetails.name}</Descriptions.Item>
            <Descriptions.Item label="Email">{guestDetails.email}</Descriptions.Item>
            <Descriptions.Item label="Mobile">{guestDetails.mobile}</Descriptions.Item>
            <Descriptions.Item label="Address">{guestDetails.address}</Descriptions.Item>
            <Descriptions.Item label="Room">{guestDetails.roomNo}</Descriptions.Item>
            <Descriptions.Item label="Booking number">{guestDetails.bookingNo}</Descriptions.Item>
            <Descriptions.Item label="Pending amount">
              <Text type="danger">
                {guestDetails.currency}
                {num(guestDetails.pendingAmount, 0).toFixed(2)}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Booking ID">{guestDetails.bookingId || "—"}</Descriptions.Item>
          </Descriptions>
        </SectionCard>
      )}

      {bookings.length > 0 && (
        <SectionCard title="Guest bookings" flush>
          <Table
            className="hms-table"
            columns={guestColumns}
            dataSource={bookings}
            rowKey={(r) => r.bookingId || r.BookingId || r.id || r.Id || Math.random()}
            pagination={false}
          />
        </SectionCard>
      )}

      <SectionCard
        title={
          <>
            Current check-ins
            <span className="hms-count">{checkins.length}</span>
          </>
        }
        flush
      >
        <Table
          className="hms-table"
          columns={checkinColumns}
          dataSource={checkins}
          rowKey="bookingId"
          loading={loading.checkins}
          pagination={tablePagination("check-in")}
          locale={{ emptyText: "No current check-ins found" }}
        />
      </SectionCard>

        <Modal
          title="Check in guest"
          open={modalVisible.checkIn}
          onCancel={() => setModalVisible((prev) => ({ ...prev, checkIn: false }))}
          footer={[
            <Button key="cancel" onClick={() => setModalVisible((prev) => ({ ...prev, checkIn: false }))}>
              Cancel
            </Button>,
            <Button key="submit" type="primary" loading={loading.checkIn} onClick={handleCheckIn}>
              Confirm check-in
            </Button>,
          ]}
          width={600}
          centered
          destroyOnHidden
        >
          <Form form={form} layout="vertical" initialValues={{ notes: guestDetails.notes, paymentAmount: guestDetails.paymentAmount }}>
            <DetailList
              items={[
                { label: "Guest", value: guestDetails.name },
                { label: "Room", value: guestDetails.roomNo },
                { label: "Booking number", value: guestDetails.bookingNo },
                {
                  label: "Pending amount",
                  value: (
                    <Text type="danger">
                      {guestDetails.currency}
                      {num(guestDetails.pendingAmount, 0).toFixed(2)}
                    </Text>
                  ),
                },
              ]}
            />

            <div style={{ height: 16 }} />

            <Form.Item label="Notes" name="notes" rules={[{ required: true, message: "Please enter notes" }]}>
              <Input.TextArea rows={3} placeholder="Enter any special requests or notes" />
            </Form.Item>

            <Form.Item
              label="Payment amount"
              name="paymentAmount"
              rules={[
                { required: num(guestDetails.pendingAmount, 0) > 0, message: "Payment is required" },
                {
                  validator: (_, value) => {
                    const pending = num(guestDetails.pendingAmount, 0);
                    const v = num(value, 0);
                    if (pending > 0 && v < pending) return Promise.reject(new Error("Amount must cover pending balance"));
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input type="number" prefix={guestDetails.currency} min={num(guestDetails.pendingAmount, 0)} step="0.01" />
            </Form.Item>

            <Form.Item label="Check-in date and time">
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm"
                value={dates.checkIn}
                onChange={(date) => setDates((prev) => ({ ...prev, checkIn: date }))}
                style={{ width: "100%" }}
              />
            </Form.Item>

            <Form.Item label="Check-out date and time">
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm"
                value={dates.checkOut}
                onChange={(date) => setDates((prev) => ({ ...prev, checkOut: date }))}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Form>
        </Modal>

        <InvoiceModal
          isOpen={modalVisible.invoice}
          onClose={() => setModalVisible((prev) => ({ ...prev, invoice: false }))}
          guestDetails={guestDetails}
          hotelDetails={hotelDetails}
          ref={invoiceRef}
          onPrint={handlePrint}
        />
    </div>
  );
};

export default CheckInOutPage;
